import { driver, DriveStep } from 'driver.js'
import i18n from 'i18next'
import { Classes } from 'jss'

import endUserManual from '../../assets/ai-go-studio-end-user-manual.pdf'
import { Settings } from '../../components/Settings/types'

const TITLE = i18n.t('Datasets')

const getDriver = (
  classesDriver: Classes<'driver'>,
  settings: Settings,
  updateSettings: (settings: Partial<Settings>) => void,
  updatePreference: () => void
) => {
  const driverSteps: DriveStep[] = [
    {
      popover: {
        title: i18n.t('Welcome to AI-go Studio!'),
        description: `
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">         
          ${i18n.t(
            'This guided tour will help you understand the main functionalities of AI-go Studio.'
          )}
          <br/><br/>
          ${i18n.t('You can always access the guided tour by clicking on the')}
          <b><i style="font-size:15px" class="fa">&#xf059;</i></b>
          ${i18n.t('icon in the upper right corner.')}
        `,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#asideDivId',
      popover: {
        title: i18n.t('Product details'),
        description: `${i18n.t(
          'Here you can find an overview of the product details, such as'
        )}:<br/>
        <ul style="padding-left:15px;">
            <li>${i18n.t('Name')}</li>
            <li>${i18n.t('Description')}</li>
            <li>${i18n.t('Date and time of creation')}</li>
            <li>${i18n.t(
              'Model type (classification, anomaly, segmentation, Patch)'
            )}</li>
            <li>${i18n.t('Classes')}</li>
            <li>${i18n.t('Labels')}</li>
            <li>${i18n.t('Number of uploaded images')}</li>
        </ul>`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#datasetsButtonId',
      popover: {
        title: TITLE,
        description: `${i18n.t('Click here to manage your datasets.')}<br/><br/>
        ${i18n.t(
          'Datasets are collections of images that you can use for training your machine learning models.'
        )}.`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#newDatasetButtonId',
      popover: {
        title: TITLE,
        description: `${i18n.t('Click here to create a new dataset')}.<br/><br/>
        ${i18n.t(
          'You can select specific images from your uploaded collection to create custom datasets for different training purposes.'
        )}.`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#datasetsListId',
      popover: {
        title: TITLE,
        description: `${i18n.t(
          'Here you can see all your datasets'
        )}! <br/><br/>
        ${i18n.t(
          'For each dataset are displayed its name, creation date, update date, number of images and actions.'
        )}.<br/><br/>
        ${i18n.t(
          'In the "actions" column you can clone, edit and delete datasets.'
        )}.<br/><br/>
        ${i18n.t(
          'Click on a dataset row to view its details and manage the images it contains.'
        )}.`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#divDatasetDetails',
      popover: {
        title: TITLE,
        description: `${i18n.t(
          'In this section are listed the info of the selected dataset'
        )}.<br/><br/>
        ${i18n.t('Click on the')} <b>${i18n.t('Edit')}</b>
        ${i18n.t('button to modify the dataset and manage its images')}.
        <br/><br/>
          ${i18n.t('Additional information can be found in the')}
          <a target='_blank' href='${endUserManual}'>
            ${i18n.t('End user manual (PDF)')}
          </a>
          <br/><br/>
          <label htmlFor="doNotShowAgainCheckbox">
            <input
              type="checkbox"
              id="doNotShowAgainCheckbox"
              ${settings.preference__show_guided_tour ? '' : 'checked'}
            />
            ${i18n.t('Do not show again')}
          </label>`,
        side: 'bottom',
        align: 'start',
        onPopoverRender: () => {
          document
            .getElementById('doNotShowAgainCheckbox')
            ?.addEventListener('change', () => {
              updateSettings({
                preference__show_guided_tour: !settings.preference__show_guided_tour,
              })
            })
        },
      },
    },
  ]

  const driverObj = driver({
    showProgress: true,
    steps: driverSteps,
    nextBtnText: i18n.t('Next'),
    prevBtnText: i18n.t('Previous'),
    doneBtnText: i18n.t('Done'),
    popoverClass: classesDriver.driver,
    onPopoverRender: (popover) => {
      const skipButton = document.createElement('button')
      skipButton.id = 'skipButton'
      skipButton.innerText = i18n.t('Skip all')
      popover.footerButtons.appendChild(skipButton)
      skipButton.addEventListener('click', () => {
        const stepsLength = driverObj.getConfig('steps')?.length
        if (stepsLength) {
          driverObj.drive(stepsLength - 1)
        }
      })
    },
    onDestroyStarted: () => {
      if (driverObj.isLastStep()) {
        updatePreference()
      }
      driverObj.destroy()
    },
  })

  return driverObj
}

export default getDriver
